import { Fragment, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { Divider, List, ListItem, ListItemText, Modal, Stack, Tabs } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import useDisclaimer from '../../hooks/useDisclaimer';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import { toast } from 'react-toastify';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function LaneList(props) {
  const theme = useTheme();
  const { laneData } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    firstName: '',
    lastName: '',
    mail: '',
    children: [],
  });

  const handleClose = () => {
    setModalOpen(false);
  };

  const personClickHandler = person => {
    let personForModal = { ...person };
    let newDate = new Date(personForModal.createdAt);
    personForModal.createdAt = newDate.toLocaleString();
    setModalData(personForModal);
    setModalOpen(true);
  };

  return (
    <>
      <Paper sx={{ margin: '0 10px' }}>
        <List sx={{ width: '300px' }}>
          <ListItem>
            <ListItemText
              primary={'Bahn ' + laneData.laneNum}
              primaryTypographyProps={{
                fontWeight: 'bold',
                fontSize: '1.2em',
                textAlign: 'center',
              }}
            ></ListItemText>
          </ListItem>
          <Divider />
          {laneData.data.map((person, i) => (
            <Fragment key={person._id}>
              <ListItem
                secondaryAction={
                  <IconButton
                    edge='end'
                    aria-label='comments'
                    onClick={e => e.stopPropagation()}
                    sx={{ display: 'none' }}
                  >
                    <TaskAltRoundedIcon />
                  </IconButton>
                }
                onClick={() => {
                  personClickHandler(person);
                }}
              >
                <ListItemText
                  primary={person.lastName + ', ' + person.firstName}
                  secondary={
                    !!person.children.length &&
                    '+ ' + person.children.map(c => c.firstName).join(', ')
                  }
                  primaryTypographyProps={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                ></ListItemText>
              </ListItem>
              <Divider />
            </Fragment>
          ))}
          <Modal open={modalOpen} onClose={handleClose}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                minWidth: 400,
                bgcolor: 'background.paper',
                border: `2px solid ${theme.palette.divider}`,
                boxShadow: 24,
                p: 4,
              }}
            >
              <Typography id='modal-modal-title' variant='h6' component='h2'>
                {modalData.lastName + ', ' + modalData.firstName}
              </Typography>
              <br />
              <Typography variant='body1'>
                <Box component='span' sx={{ fontWeight: 600 }}>
                  eMail:
                </Box>{' '}
                {modalData.mail}
              </Typography>
              {!!modalData.children.length && (
                <>
                  <br />
                  <Typography variant='body1' fontWeight='bold'>
                    Kinder
                  </Typography>
                  <ul style={{ marginBlockStart: 0, marginBlockEnd: 0 }}>
                    {modalData.children.map(child => (
                      <li key={child._id}>{child.lastName + ', ' + child.firstName}</li>
                    ))}
                  </ul>
                </>
              )}
              <br />
              <Typography variant='body1'>
                <Box component='span' sx={{ fontWeight: 600 }}>
                  Zeitpunkt:
                </Box>{' '}
                {modalData.createdAt}
              </Typography>
              <br />
              <Typography variant='body1' fontWeight='bold'>
                Unterschrift
              </Typography>
              <br />
              <Paper
                sx={{
                  backgroundColor: theme.palette.common.white,
                  height: '150px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box>
                    <img
                      src={modalData.signature}
                      style={{
                        backgroundColor: theme.palette.common.white,
                        maxWidth: '400px',
                        margin: 'auto',
                        padding: '5px',
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Modal>
        </List>
      </Paper>
    </>
  );
}

export default function Waivers() {
  const [data, setData] = useState([]);
  const { getUnseenDisclaimers, error } = useDisclaimer();

  const fetchData = async () => {
    const disclaimers = await getUnseenDisclaimers();
    setData(disclaimers);
  };

  useEffect(() => {
    fetchData().catch(e => {
      toast.error('Fehler beim Laden');
      console.error(e);
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(
      () => {
        console.log('update');
        fetchData().catch(e => {
          console.error(e);
          toast.error('Fehler beim Laden');
        });
      },
      1 * 60 * 1000
    );
    return () => clearInterval(interval);
  }, []);
  return (
    <Box sx={viewRootTextPrimarySx}>
      {!!data.length ? (
        <>
          <Tabs variant='scrollable' scrollButtons={false} value={false}>
            {data.map(lane => (
              <LaneList laneData={lane} key={lane.laneNum} />
            ))}
          </Tabs>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            sx={{ width: '100%', marginTop: '50px' }}
          >
            <KeyboardDoubleArrowLeftIcon sx={{ marginRight: '10px' }} />
            <Typography variant='body1' component='div' align='center'>
              Scrollen um alle Bahnen zu sehen
            </Typography>
            <KeyboardDoubleArrowRightIcon sx={{ marginLeft: '10px' }} />
          </Stack>
        </>
      ) : (
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='center'
          sx={{ width: '100%', marginTop: '50px' }}
        >
          <HourglassTopRoundedIcon sx={{ fontSize: '100', marginRight: '10px' }} />
          <Typography variant='h4' component='div' align='center'>
            Warte auf neue Daten...
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
